import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import Logo from '../ui/Logo';
import styles from './Navbar.module.css';
import CartDrawer from '../cart/CartDrawer';
import { useCartStore } from '../../store/cartStore';
import { useAuth } from '../../hooks/useAuth';
import { useProfile } from '../../hooks/useUserData';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  // Close the mobile menu on route change. Adjusted during render (not in an
  // effect) per https://react.dev/learn/you-might-not-need-an-effect — avoids
  // an extra render where the menu would briefly still show the old route.
  const [prevLocationKey, setPrevLocationKey] = useState(location.key);
  if (location.key !== prevLocationKey) {
    setPrevLocationKey(location.key);
    setMenuOpen(false);
  }
  const { itemCount, openCart, isOpen } = useCartStore();
  const { isLoggedIn, logout, displayName, avatarInitials, userId } = useAuth();
  const { data: profile } = useProfile(userId);
  const count = itemCount();
  const tierLabel = profile?.subscription_tier
    ? profile.subscription_tier.charAt(0).toUpperCase() + profile.subscription_tier.slice(1)
    : 'Free';

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const handleLogin  = () => navigate('/login');
  const handleSignup = () => navigate('/signup');
  const handleLogout = () => logout();

  return (
    <>
      {/* Logged-in context bar */}
      {isLoggedIn && (
        <div className={styles.accountBar}>
          <div className={styles.accountBarLeft}>
            <div className={styles.accountBarUser}>
              <div className={styles.accountBarAvatar}>{avatarInitials}</div>
              <span>{displayName}</span>
            </div>
            {profile?.is_seller && (
              <>
                <span>·</span>
                <span>{tierLabel} Seller</span>
              </>
            )}
          </div>
          <div className={styles.accountBarRight}>
            <Link to="/account/purchases"       className={styles.accountBarLink}>My Orders</Link>
            <Link to="/account/wishlist"        className={styles.accountBarLink}>Wishlist</Link>
            <Link to="/dashboard/notifications" className={styles.accountBarLink}>Notifications</Link>
            <Link to="/dashboard/wallet"        className={styles.accountBarLink}>Wallet</Link>
            <Link to="/dashboard/settings"      className={styles.accountBarLink}>Settings</Link>
            <Link to="/dashboard" className={styles.accountBarDashBtn}>Seller Dashboard →</Link>
            <button className={styles.accountBarLogout} onClick={handleLogout}>Sign out</button>
          </div>
        </div>
      )}

      {/* Main marketplace header */}
      <header className={[styles.navbar, scrolled ? styles.solid : '', isLoggedIn ? styles.navbarWithBar : ''].join(' ')}>
        <div className={styles.inner}>
          <Link to="/" className={styles.brand}>
            <Logo size={34} />
          </Link>

          <nav className={[styles.nav, menuOpen ? styles.navOpen : ''].join(' ')}>
            <NavLink to="/explore" className={({ isActive }) => [styles.navLink, isActive ? styles.active : ''].join(' ')}>
              Explore
            </NavLink>
            <NavLink to="/pricing" className={({ isActive }) => [styles.navLink, isActive ? styles.active : ''].join(' ')}>
              Pricing
            </NavLink>
            <NavLink to="/docs" className={({ isActive }) => [styles.navLink, isActive ? styles.active : ''].join(' ')}>
              Documentation
            </NavLink>
            <NavLink to="/sellers" className={({ isActive }) => [styles.navLink, isActive ? styles.active : ''].join(' ')}>
              Sellers
            </NavLink>
          </nav>

          <div className={styles.actions}>
            {/* ⌘K command palette button */}
            <button
              className={styles.cmdBtn}
              onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }))}
              aria-label="Open command palette (⌘K)"
            >
              <span className={styles.cmdBtnText}>Search commands...</span>
              <kbd className={styles.cmdBtnKbd}>⌘K</kbd>
            </button>

            <button
              className={[styles.cartBtn, count > 0 ? styles.cartBtnActive : ''].join(' ')}
              onClick={openCart}
              aria-label={`Cart (${count} items)`}
            >
              <CartIcon />
              {count > 0 && <span className={styles.cartBadge}>{count}</span>}
            </button>

            {!isLoggedIn && (
              <>
                <button className={styles.loginBtn} onClick={handleLogin}>Log in</button>
                <button className={styles.signupBtn} onClick={handleSignup}>Start Selling</button>
              </>
            )}
          </div>

          <button className={styles.menuToggle} onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
            <span className={[styles.menuBar, menuOpen ? styles.bar1Open : ''].join(' ')} />
            <span className={[styles.menuBar, menuOpen ? styles.bar2Open : ''].join(' ')} />
            <span className={[styles.menuBar, menuOpen ? styles.bar3Open : ''].join(' ')} />
          </button>
        </div>
      </header>

      {isOpen && <CartDrawer />}
    </>
  );
}

function CartIcon() {
  return <ShoppingCart size={19} strokeWidth={1.8} />;
}
